import { injectable } from 'inversify';
import { trace, Link, Span } from '@dealroadshow/jsbro/OTel/api';

export const PAGE_TRACE_MAX_AGE_MS = 5 * 60 * 1000;

export const PAGE_LOAD_REDIRECT_WINDOW_MS = 1000;

@injectable()
class PageTrace {
  private span: Span | null = null;

  private pageLoadSpan: Span | null = null;

  private pathname: string | null = null;

  private route: () => string = () => '';

  private startedAt = 0;

  private isUsed = false;

  public navigate(pathname: string, route: () => string, pageLoadSpan?: Span): void {
    this.route = route;

    if (pageLoadSpan && pageLoadSpan !== this.pageLoadSpan) {
      this.pageLoadSpan = pageLoadSpan;
      this.start(pathname, pageLoadSpan);
      return;
    }

    if (this.pathname === pathname && !this.isExpired()) {
      return;
    }

    if (this.isRedirectAfterPageLoad()) {
      this.pathname = pathname;
      return;
    }

    this.start(pathname, null);
  }

  public isStarted(): boolean {
    return this.pathname !== null;
  }

  public getSpan(): Span | undefined {
    if (!this.isStarted() || this.isExpired()) {
      return undefined;
    }

    this.isUsed = true;
    return this.getOrStartSpan();
  }

  public getLink(): Link | undefined {
    if (!this.isStarted() || !this.isExpired()) {
      return undefined;
    }

    return { context: this.getOrStartSpan().spanContext() };
  }

  private start(pathname: string, span: Span | null): void {
    this.span = span;
    this.pathname = pathname;
    this.startedAt = Date.now();
    this.isUsed = false;
  }

  private getOrStartSpan(): Span {
    if (!this.span) {
      this.span = trace.getTracer().startSpan('page.navigation', {
        root: true,
        startTime: this.startedAt,
        attributes: { page: this.route() },
      });
      this.span.end(this.startedAt);
    }

    return this.span;
  }

  private isRedirectAfterPageLoad(): boolean {
    return this.span !== null
      && this.span === this.pageLoadSpan
      && !this.isUsed
      && Date.now() - this.startedAt < PAGE_LOAD_REDIRECT_WINDOW_MS;
  }

  private isExpired(): boolean {
    return Date.now() - this.startedAt >= PAGE_TRACE_MAX_AGE_MS;
  }
}

export default PageTrace;
